import React, { useState, useEffect } from 'react';
import ApiService from '../../services/api';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Grid,
  CircularProgress,
  List,
  IconButton,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import API_CONFIG from '../../config/api';

const SubExerciseForm = ({ open, handleClose, exerciseId, token, onSubExerciseAdded }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', description: '', instructions: '', videoUrl: '', order: ''
  });
  const [gifImage, setGifImage] = useState(null);
  const [gifGirlImage, setGifGirlImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewGirlUrl, setPreviewGirlUrl] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [subExercises, setSubExercises] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingSubExerciseId, setEditingSubExerciseId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subExerciseToDelete, setSubExerciseToDelete] = useState(null);
  const [fetchingSubExercises, setFetchingSubExercises] = useState(false);

  useEffect(() => { if (open && exerciseId) fetchSubExercises(); }, [open, exerciseId, token]);

  const fetchSubExercises = async () => {
    try {
      setFetchingSubExercises(true);
      const res = await ApiService.get(`/api/admin/exercises/${exerciseId}/sub-exercises`, {
        'Authorization': `Bearer ${token}`
      });
      const data = await res.json();
      if (data.success) setSubExercises([...data.subExercises].sort((a, b) => a.order - b.order));
      else toast.error(data.message || 'Failed to fetch sub-exercises');
    } catch (e) {
      toast.error('Something went wrong.');
    } finally {
      setFetchingSubExercises(false);
    }
  };

  const handleEditSubExercise = (item) => {
    setEditMode(true);
    setEditingSubExerciseId(item._id);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      instructions: item.instructions || '',
      order: item.order || '',
      videoUrl: item.videoUrl || ''
    });
    setPreviewUrl(item.gifUrl || '');
    setPreviewGirlUrl(item.gifUrlGirl || '');
    setTabValue(0);
  };

  const handleDeleteConfirm = async () => {
    if (!subExerciseToDelete) return;
    try {
      setLoading(true);
      const res = await ApiService.delete(`/api/admin/sub-exercises/${subExerciseToDelete._id}`, {
        'Authorization': `Bearer ${token}`
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Deleted');
        fetchSubExercises();
        onSubExerciseAdded?.();
      } else toast.error(data.message || 'Failed to delete');
    } catch (e) {
      toast.error('Something went wrong.');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setSubExerciseToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', instructions: '', order: '', videoUrl: '' });
    setGifImage(null); setGifGirlImage(null);
    setPreviewUrl(''); setPreviewGirlUrl('');
    setEditMode(false); setEditingSubExerciseId(null);
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleFileChange = (e, isGirl = false) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Only image files allowed');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => isGirl ? setPreviewGirlUrl(reader.result) : setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
    isGirl ? setGifGirlImage(file) : setGifImage(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description) {
      toast.error('Fill all required fields');
      return;
    }
    try {
      setLoading(true);
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => v && data.append(k, v));
      if (gifImage) data.append('gifImage', gifImage);
      if (gifGirlImage) data.append('gifImageGirl', gifGirlImage);
      const url = editMode ? `/api/admin/sub-exercises/${editingSubExerciseId}` : `/api/admin/exercises/${exerciseId}/sub-exercises`;
      const method = editMode ? 'PUT' : 'POST';
      const res = await ApiService.fetchApi(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: data
      });
      const result = await res.json();
      if (result.success) {
        toast.success(editMode ? 'Updated' : 'Added');
        resetForm(); fetchSubExercises(); onSubExerciseAdded?.();
        setTabValue(1);
      } else toast.error(result.message || 'Failed to save');
    } catch (err) {
      toast.error('Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>{editMode ? 'Edit Sub-Exercise' : 'Manage Sub-Exercises'}</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="fullWidth">
            <Tab label={editMode ? 'Edit Sub-Exercise' : 'Add Sub-Exercise'} />
            <Tab label="Sub-Exercise List" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required label="Name" name="name" value={formData.name} onChange={handleInputChange} disabled={loading} margin="normal" />
                <TextField fullWidth required multiline rows={4} label="Description" name="description" value={formData.description} onChange={handleInputChange} disabled={loading} margin="normal" />
                <TextField fullWidth multiline rows={4} label="Instructions" name="instructions" value={formData.instructions} onChange={handleInputChange} disabled={loading} margin="normal" />
                <TextField fullWidth label="Video URL" name="videoUrl" value={formData.videoUrl} onChange={handleInputChange} disabled={loading} margin="normal" />
                <TextField fullWidth label="Order" name="order" type="number" value={formData.order} onChange={handleInputChange} disabled={loading} helperText="Leave blank for auto order" margin="normal" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mt: 2 }}>
                  <Typography>Upload Main Image</Typography>
                  <input accept="image/*" id="gif-upload" type="file" style={{ display: 'none' }} onChange={(e) => handleFileChange(e, false)} disabled={loading} />
                  <label htmlFor="gif-upload"><Button variant="contained" component="span" disabled={loading}>Upload</Button></label>
                </Box>
                {previewUrl && <Box component="img" src={API_CONFIG.baseURL + previewUrl} sx={{ mt: 1, maxHeight: 150, border: '1px solid #ccc', borderRadius: 1 }} alt="Preview" />}
                <Box sx={{ mt: 4 }}>
                  <Typography>Upload Girl Image</Typography>
                  <input accept="image/*" id="gif-girl-upload" type="file" style={{ display: 'none' }} onChange={(e) => handleFileChange(e, true)} disabled={loading} />
                  <label htmlFor="gif-girl-upload"><Button variant="contained" component="span" disabled={loading}>Upload</Button></label>
                </Box>
                {previewGirlUrl && <Box component="img" src={API_CONFIG.baseURL + previewGirlUrl} sx={{ mt: 1, maxHeight: 150, border: '1px solid #ccc', borderRadius: 1 }} alt="Girl Preview" />}
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              {editMode && <Button onClick={resetForm} sx={{ mr: 1 }} disabled={loading}>Cancel</Button>}
              <Button type="submit" variant="contained" disabled={loading} startIcon={loading && <CircularProgress size={20} />}>
                {loading ? 'Saving...' : editMode ? 'Update' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            {fetchingSubExercises ? <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box> :
              subExercises.length === 0 ? <Typography align="center">No sub-exercises added yet.</Typography> :
                <List>
                  {subExercises.map((item, idx) => (
                    <Paper key={item._id} sx={{ mb: 2, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <Typography variant="h6">{idx + 1}. {item.name}</Typography>
                          <Typography color="text.secondary">{item.description}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          {item.gifUrl && <Box component="img" src={API_CONFIG.baseURL + item.gifUrl} sx={{ maxHeight: 100, objectFit: 'contain' }} alt="gif" />}
                          {item.gifUrlGirl && <Box component="img" src={API_CONFIG.baseURL + item.gifUrlGirl} sx={{ maxHeight: 100, objectFit: 'contain', ml: 1 }} alt="girl gif" />}
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <IconButton onClick={() => handleEditSubExercise(item)} color="primary"><EditIcon /></IconButton>
                          <IconButton onClick={() => { setSubExerciseToDelete(item); setDeleteDialogOpen(true); }} color="error"><DeleteIcon /></IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </List>}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => { resetForm(); setTabValue(0); }} disabled={loading}>Add New</Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Close</Button>
      </DialogActions>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this sub-exercise?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={loading} startIcon={loading && <CircularProgress size={20} />}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SubExerciseForm;
